import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface CachedIcon {
  url: string;
  blob: Blob;
  timestamp: number;
  accessCount: number;
  lastAccessed: number;
}

interface CacheStats {
  size: number;
  hitRate: number;
  totalRequests: number;
  cacheHits: number;
  memoryUsage: number;
}

interface UseIconCacheReturn {
  getCachedIcon: (iconPath: string) => Promise<string>;
  preloadToCache: (iconPaths: string[]) => Promise<void>;
  clearCache: () => void;
  getCacheStats: () => CacheStats;
  isInCache: (iconPath: string) => boolean;
  removeLeastUsed: (count?: number) => void;
}

/**
 * Hook for caching icons in memory for optimal performance
 */
export const useIconCache = (maxCacheSize: number = 50): UseIconCacheReturn => {
  const [cache, setCache] = useState<Map<string, CachedIcon>>(new Map());
  const [stats, setStats] = useState({
    totalRequests: 0,
    cacheHits: 0
  });
  
  const cacheRef = useRef<Map<string, CachedIcon>>(new Map());
  const objectUrlsRef = useRef<Set<string>>(new Set());

  // Keep refs in sync
  useEffect(() => {
    cacheRef.current = cache;
  }, [cache]);

  // Get public URL for an icon
  const getIconUrl = useCallback((iconPath: string): string => {
    const { data } = supabase.storage
      .from('app-icons')
      .getPublicUrl(iconPath);
    return data.publicUrl;
  }, []);

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach(url => {
        URL.revokeObjectURL(url);
      });
      objectUrlsRef.current.clear();
    };
  }, []);

  // Remove least recently used items from cache
  const removeLeastUsed = useCallback((count: number = 1) => {
    setCache(currentCache => {
      const entries = Array.from(currentCache.entries());
      
      // Sort by access count (ascending) then by last accessed (ascending)
      entries.sort((a, b) => {
        if (a[1].accessCount !== b[1].accessCount) {
          return a[1].accessCount - b[1].accessCount;
        }
        return a[1].lastAccessed - b[1].lastAccessed;
      });

      // Remove least used items
      const toRemove = entries.slice(0, count);
      toRemove.forEach(([key, cachedIcon]) => {
        if (objectUrlsRef.current.has(cachedIcon.url)) {
          URL.revokeObjectURL(cachedIcon.url);
          objectUrlsRef.current.delete(cachedIcon.url);
        }
      });

      const newCache = new Map(currentCache);
      toRemove.forEach(([key]) => {
        newCache.delete(key);
      });

      return newCache;
    });
  }, []);

  // Get cached icon or fetch and cache it
  const getCachedIcon = useCallback(async (iconPath: string): Promise<string> => {
    setStats(prev => ({ 
      ...prev, 
      totalRequests: prev.totalRequests + 1 
    }));

    const cachedIcon = cacheRef.current.get(iconPath);
    
    if (cachedIcon) {
      // Update access stats
      const updatedIcon = {
        ...cachedIcon,
        accessCount: cachedIcon.accessCount + 1,
        lastAccessed: Date.now()
      };
      
      setCache(prev => new Map(prev.set(iconPath, updatedIcon)));
      setStats(prev => ({ 
        ...prev, 
        cacheHits: prev.cacheHits + 1 
      }));
      
      return cachedIcon.url;
    }

    // Not in cache - fetch it
    try {
      const publicUrl = getIconUrl(iconPath);
      const response = await fetch(publicUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch icon: ${response.statusText}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      objectUrlsRef.current.add(objectUrl);

      const cachedIcon: CachedIcon = {
        url: objectUrl,
        blob,
        timestamp: Date.now(),
        accessCount: 1,
        lastAccessed: Date.now()
      };

      // Check cache size and remove items if necessary
      if (cacheRef.current.size >= maxCacheSize) {
        removeLeastUsed(Math.ceil(maxCacheSize * 0.1)); // Remove 10% of cache
      }

      setCache(prev => new Map(prev.set(iconPath, cachedIcon)));
      return objectUrl;

    } catch (error) {
      console.error(`Failed to cache icon ${iconPath}:`, error);
      // Fallback to direct URL
      return getIconUrl(iconPath);
    }
  }, [getIconUrl, maxCacheSize, removeLeastUsed]);

  // Preload multiple icons to cache
  const preloadToCache = useCallback(async (iconPaths: string[]): Promise<void> => {
    const promises = iconPaths.map(async (iconPath) => {
      try {
        await getCachedIcon(iconPath);
      } catch (error) {
        console.warn(`Failed to preload icon to cache: ${iconPath}`, error);
      }
    });

    await Promise.allSettled(promises);
  }, [getCachedIcon]);

  // Clear entire cache
  const clearCache = useCallback(() => {
    // Revoke all object URLs
    objectUrlsRef.current.forEach(url => {
      URL.revokeObjectURL(url);
    });
    objectUrlsRef.current.clear();
    
    setCache(new Map());
    setStats({ totalRequests: 0, cacheHits: 0 });
    
    console.log('🧹 Icon cache cleared');
  }, []);

  // Check if icon is in cache
  const isInCache = useCallback((iconPath: string): boolean => {
    return cacheRef.current.has(iconPath);
  }, []);

  // Get cache statistics
  const getCacheStats = useCallback((): CacheStats => {
    const cacheSize = cache.size;
    const hitRate = stats.totalRequests > 0 ? (stats.cacheHits / stats.totalRequests) * 100 : 0;
    
    // Calculate approximate memory usage
    let memoryUsage = 0;
    cache.forEach(cachedIcon => {
      memoryUsage += cachedIcon.blob.size;
    });

    return {
      size: cacheSize,
      hitRate: Math.round(hitRate * 100) / 100,
      totalRequests: stats.totalRequests,
      cacheHits: stats.cacheHits,
      memoryUsage: Math.round(memoryUsage / 1024) // In KB
    };
  }, [cache, stats]);

  return {
    getCachedIcon,
    preloadToCache,
    clearCache,
    getCacheStats,
    isInCache,
    removeLeastUsed
  };
};

export default useIconCache;